import {
  SignalIcon,
  GlobeAltIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  LightBulbIcon,
  Cog6ToothIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

function TelcoHeroSection() {
  return (
    <section className="relative overflow-hidden px-6 pb-32 pt-40">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(16,185,129,0.15),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(16,185,129,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(16,185,129,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="mx-auto max-w-6xl text-left text-gray-800">
        {/* Caption */}
        <span className="animate-fadeUp mb-4 block text-sm font-medium tracking-wide text-green-500">
          Connecting People & Business
        </span>

        {/* Main Heading */}
        <h1 className="mb-8 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
          <span className="animate-fadeUp block dark:text-gray-100">Telco Industry Innovation</span>
          <span className="animate-fadeUp animate-gradientFlow mt-2 block bg-gradient-to-r from-green-400 via-teal-400 to-blue-400 bg-clip-text text-transparent delay-150">
            Networks That Empower
          </span>
        </h1>

        {/* Subheading */}
        <p className="animate-fadeUp max-w-2xl text-base leading-relaxed delay-300 sm:text-lg dark:text-gray-100/90">
          We build telecommunication solutions that scale globally, enhance connectivity, and drive
          digital transformation.
        </p>

        {/* Key Highlights */}
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:gap-8">
          <div className="flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 shadow-md backdrop-blur-lg">
            <SignalIcon className="h-6 w-6 text-green-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              5G & IoT Ready
            </span>
          </div>
          <div className="flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 shadow-md backdrop-blur-lg">
            <GlobeAltIcon className="h-6 w-6 text-teal-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Global Connectivity
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function TelcoGrowth() {
  const stats = [
    {
      label: 'Global Mobile Subscriptions',
      value: 8300,
      suffix: 'M+',
      icon: GlobeAltIcon,
    },
    {
      label: '5G Connections',
      value: 2800,
      suffix: 'M+',
      icon: DevicePhoneMobileIcon,
    },
    {
      label: 'Revenue Growth (CAGR)',
      value: 7.5,
      suffix: '%',
      icon: ChartBarIcon,
    },
  ];

  return (
    <section className="relative mx-auto max-w-6xl px-6 py-24">
      {/* Header */}
      <div className="mb-16 text-center">
        <h2 className="bg-gradient-to-r from-green-600 via-teal-500 to-blue-600 bg-clip-text text-3xl font-extrabold text-transparent sm:text-4xl">
          How the Telco Industry Is Growing
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300">
          The telecommunications sector continues to expand rapidly, driven by 5G deployment,
          digital services, and an ever‑increasing demand for connectivity and data.
        </p>
      </div>

      {/* Growth Stats */}
      <div className="grid gap-8 sm:grid-cols-3">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="group relative flex flex-col items-center gap-4 rounded-3xl bg-gradient-to-br from-green-50 via-green-100 to-green-50 p-8 shadow-lg backdrop-blur-xl transition-transform duration-300 hover:scale-105 hover:shadow-2xl dark:from-gray-800 dark:via-gray-900 dark:to-gray-800"
            >
              <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-tr from-green-400 via-teal-400 to-blue-400 text-white shadow-md transition-all duration-500 group-hover:scale-110">
                <Icon className="h-8 w-8" />
                {/* Subtle glowing effect */}
                <span className="absolute -inset-2 animate-pulse rounded-full bg-gradient-to-r from-green-300 via-teal-300 to-blue-300 opacity-20 blur-xl" />
              </div>
              <div className="text-3xl font-extrabold text-gray-900 dark:text-white">
                <CountUp end={stat.value} duration={2} suffix={stat.suffix} />
              </div>
              <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                {stat.label}
              </p>
            </div>
          );
        })}
      </div>

      {/* Growth Notes */}
      <div className="mx-auto mt-16 max-w-4xl space-y-4 text-center text-sm leading-relaxed text-gray-700 sm:text-base dark:text-gray-300">
        <p>
          The telecommunications landscape is driven by explosive mobile data adoption, widespread
          5G expansion, and the rising demand for digital services like IoT, cloud connectivity, and
          edge computing. Telcos are transitioning from traditional voice services to scalable
          digital platforms.
        </p>
        <p>
          With connectivity now essential to business operations, remote work, entertainment, and
          real‑time services, the industry’s growth is expected to continue accelerating over the
          next decade.
        </p>
      </div>
    </section>
  );
}

function WhatWeDoSection() {
  const activities = [
    {
      icon: LightBulbIcon,
      title: 'User-Focused Solutions',
      description:
        'We design products with real users in mind, ensuring usability, accessibility, and meaningful impact.',
      color: 'from-purple-400 to-pink-500',
    },
    {
      icon: Cog6ToothIcon,
      title: 'Research & Development',
      description:
        'Continuous innovation is at our core, with dedicated teams exploring AI, data, and engineering advancements.',
      color: 'from-indigo-400 to-blue-500',
    },
    {
      icon: CubeTransparentIcon,
      title: 'AI and Data Driven Solutions',
      description:
        'Telcom solutions powered by AI and data analytics to optimize network performance and customer experiences and drive intelligent decision-making meanwhile reducing operational costs.',
      color: 'from-yellow-400 to-orange-500',
    },
    {
      icon: ChartBarIcon,
      title: 'Data-Driven Strategy',
      description:
        'Our solutions leverage analytics and insights to drive smarter decisions and measurable business outcomes.',
      color: 'from-green-400 to-lime-500',
    },
  ];

  return (
    <section className="relative mx-auto max-w-6xl px-6 py-32">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(16,185,129,0.15),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(16,185,129,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(16,185,129,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>
      {/* Background blobs */}
      <div className="animate-slowFloat absolute -left-40 -top-40 h-96 w-96 rounded-full bg-purple-600/20 blur-3xl" />
      <div className="animate-slowFloat2 absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-pink-500/20 blur-3xl" />

      {/* Header */}
      <div className="relative z-10 mb-20 text-center">
        <h2 className="bg-gradient-to-r from-green-600 via-teal-500 to-blue-600 bg-clip-text text-3xl font-extrabold text-transparent sm:text-4xl">
          What We Do
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300">
          Our expertise spans multiple areas, from human-centered product design to advanced AI
          research and strategy.
        </p>
      </div>

      {/* Activities */}
      <div className="relative z-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {activities.map((act, index) => {
          const Icon = act.icon;
          return (
            <motion.div
              key={act.title}
              className="group relative flex flex-col items-center gap-5 rounded-3xl bg-white/90 p-8 shadow-xl backdrop-blur-xl transition-transform duration-300 hover:scale-105 dark:bg-gray-800"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <div
                className={`relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-tr ${act.color} text-white shadow-lg transition-all duration-500 group-hover:scale-110`}
              >
                <Icon className="h-10 w-10" />
                <span className="absolute -inset-2 animate-pulse rounded-full bg-gradient-to-r from-white/20 via-white/10 to-white/20 opacity-30 blur-xl" />
              </div>
              <h3 className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-center text-xl font-extrabold text-transparent">
                {act.title}
              </h3>
              <p className="text-center text-sm leading-relaxed text-gray-700 sm:text-base dark:text-gray-300">
                {act.description}
              </p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function TelcoFinalCTA() {
  const navigate = useNavigate();

  return (
    <section className="relative mx-auto flex max-w-6xl flex-col items-center justify-center overflow-hidden rounded-3xl px-6 py-32 text-center">
      {/* Content */}
      <h2 className="relative z-10 text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-gray-100">
        Hire the Best Engineering Team
        <span className="mt-2 block bg-gradient-to-r from-green-600 via-teal-500 to-blue-600 bg-clip-text text-transparent">
          to Transform Your Telco Business
        </span>
      </h2>
      <p className="relative z-10 mt-4 max-w-2xl text-lg text-gray-700 dark:text-gray-300">
        Our expert engineers build scalable, reliable, and cutting-edge solutions tailored for the
        telecommunications sector. Let's accelerate your growth, optimize operations, and bring your
        ideas to life.
      </p>

      {/* Button */}
      <button
        onClick={() => navigate('/contacts')}
        className="relative z-10 mt-8 flex items-center gap-3 rounded-full bg-gradient-to-r from-green-600 via-teal-500 to-blue-600 px-8 py-4 text-sm font-semibold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
      >
        Contact Our Team
        <ArrowRightIcon className="h-5 w-5" />
      </button>
    </section>
  );
}
export default function Telcom() {
  return (
    <div className="mx-auto sm:px-6 lg:px-8">
      <TelcoHeroSection />
      <TelcoGrowth />
      <WhatWeDoSection />
      <TelcoFinalCTA />
    </div>
  );
}
